import React, { useMemo } from "react";
import { Stack } from "@mui/material";
import moment from "moment";
import { ActionModal, EventDetailsModal, NoticeboardButton } from "../shared";
import type { Notice } from "../../services/api/types";
import type { NoticeCalendarEventKind } from "../../utils/noticeCalendarEvents";
import CalendarActivityList from "./CalendarActivityList";
import type { CalendarActivityItem } from "./UpcomingActivityPanel";

export type CalendarDayEventSelection = {
  /** Unique per calendar item — a notice's event and publish marker differ. */
  eventKey: string;
  kind: NoticeCalendarEventKind;
  noticeId: string;
  notice: Notice;
  fullTitle: string;
  start: Date;
  allDay?: boolean;
};

type CalendarDayEventsModalProps = {
  open: boolean;
  onClose: () => void;
  dayDate: Date | null;
  events: CalendarDayEventSelection[];
  selectedEvent: CalendarDayEventSelection | null;
  onSelectEvent: (event: CalendarDayEventSelection) => void;
  onBackToList: () => void;
  allNotices?: Notice[];
  getEventColor: (noticeId: string) => string;
};

const CalendarDayEventsModal: React.FC<CalendarDayEventsModalProps> = ({
  open,
  onClose,
  dayDate,
  events,
  selectedEvent,
  onSelectEvent,
  onBackToList,
  allNotices,
  getEventColor,
}) => {
  const isListView = !selectedEvent;

  const activityItems = useMemo((): CalendarActivityItem[] => {
    return events.map((event) => ({
      eventKey: event.eventKey,
      noticeId: event.noticeId,
      fullTitle: event.fullTitle,
      start: event.start,
      kind: event.kind,
      isStaffOnly: Boolean(event.notice.staffOnly),
      notice: event.notice,
    }));
  }, [events]);

  const title = isListView
    ? dayDate
      ? moment(dayDate).format("dddd, D MMMM YYYY")
      : "Events"
    : "";

  const subtitle = isListView
    ? `${events.length} item${events.length === 1 ? "" : "s"} on this day`
    : undefined;

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      maxWidth={isListView ? "sm" : "md"}
      headerStyle={isListView ? "default" : "minimal"}
      denseContent={!isListView}
      dialogContentSx={{ minHeight: "auto" }}
      footer={
        selectedEvent && events.length > 1 ? (
          <Stack direction="row" justifyContent="flex-start">
            <NoticeboardButton variant="secondary" size="medium" onClick={onBackToList}>
              Back to events
            </NoticeboardButton>
          </Stack>
        ) : undefined
      }
    >
      {isListView ? (
        <CalendarActivityList
          items={activityItems}
          getEventColor={getEventColor}
          onItemClick={onSelectEvent}
        />
      ) : (
        <EventDetailsModal event={selectedEvent} allNotices={allNotices} />
      )}
    </ActionModal>
  );
};

export default CalendarDayEventsModal;
